import {
  capitalizeFirst,
  formatText,
  formatWeightLb,
  getServiceTypeDisplayLabel,
  ISOStringToShorthandDate,
  toFullReadableAddress,
} from "utils";

interface Item {
  label: string;
  value: string;
}

function getDetailsSection(
  shipment: PublicShipment,
  includeCarrierInfo: boolean,
): AppRenderedPDFSectionConfig {
  const { info } = shipment;
  const items: Item[] = [];

  if (includeCarrierInfo && shipment.booked_carrier) {
    if (shipment.booked_carrier_company?.name) {
      items.push({
        label: "Carrier Company",
        value: shipment.booked_carrier_company.name,
      });
    }
    if (shipment.booked_carrier.user) {
      const carrierUser = shipment.booked_carrier.user;
      items.push({
        label: "Carrier Contact",
        value: `${carrierUser.first_name} ${carrierUser.last_name}`,
      });
    }
  }

  items.push({
    label: "Shipper Company",
    value: shipment.supplier_company?.name || "",
  });

  if (shipment.supplier?.user) {
    items.push({
      label: "Shipper Contact",
      value: `${shipment.supplier.user.first_name} ${shipment.supplier.user.last_name}`,
    });
  }

  items.push(
    {
      label: "Commodity",
      value: info.commodity_freetext
        ? capitalizeFirst(formatText(info.commodity_freetext))
        : "General Freight",
    },
    {
      label: "Weight",
      value:
        info.weight_lb != null
          ? formatWeightLb(info.weight_lb, { includeKg: false })
          : "",
    },
    {
      label: "Service",
      value: getServiceTypeDisplayLabel(info.service ?? ""),
    },
    {
      label: "Special Requirements",
      value: info.special_requirements || "",
    },
    {
      label: "Additional Details",
      value: info.additional_details || "",
    },
  );

  if (info.footage) {
    items.push({
      label: "Footage",
      value: `${info.footage} ft`,
    });
  }

  return {
    id: "public-shipment-details",
    title: "Shipment Details",
    items,
  };
}

function getRouteSection(
  shipment: PublicShipment,
): AppRenderedPDFSectionConfig {
  const items: Item[] = [];
  const points = shipment.route_points;
  const etasByPointId = new Map(
    shipment.etas.map((eta) => [eta.point.id, eta]),
  );

  const originPoint = points.find((p) => p.stop_type === "ORIGIN");
  const destinationPoint = points.find((p) => p.stop_type === "DESTINATION");
  const middlePoints = points
    .filter((p) => p.stop_type === "MID")
    .sort((a, b) => a.point_number - b.point_number);

  if (originPoint) {
    items.push({
      label: "Origin",
      value: toFullReadableAddress(originPoint.point_location) || "",
    });
    const originETA =
      originPoint.id !== undefined
        ? etasByPointId.get(originPoint.id)
        : undefined;
    if (originETA?.eta) {
      items.push({
        label: "Origin Date ETA (dd/mm/yyyy)",
        value: ISOStringToShorthandDate(originETA.eta),
      });
    }
  }

  middlePoints.forEach((point) => {
    items.push({
      label: `Stop ${point.point_number - 1} Location`,
      value: toFullReadableAddress(point.point_location) || "",
    });
  });

  if (destinationPoint) {
    items.push({
      label: "Destination",
      value: toFullReadableAddress(destinationPoint.point_location) || "",
    });
    const destETA =
      destinationPoint.id !== undefined
        ? etasByPointId.get(destinationPoint.id)
        : undefined;
    if (destETA?.eta) {
      items.push({
        label: "Destination Date ETA (dd/mm/yyyy)",
        value: ISOStringToShorthandDate(destETA.eta),
      });
    }
  }

  return {
    id: "public-shipment-route",
    title: "Shipment Route",
    items,
  };
}

export function getPublicShipmentExportPDFRenderConfig(
  shipment: PublicShipment,
  includeCarrierInfo: boolean,
): AppRenderedPDFSectionConfig[] {
  return [
    getDetailsSection(shipment, includeCarrierInfo),
    getRouteSection(shipment),
  ];
}
